import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { authApi, profileApi } from '../api/endpoints'
import { ApiError, getToken, setToken as persistToken } from '../api/client'
import type {
  AddProfileRequest,
  AddRoleRequest,
  CodeSentResponse,
  LoginRequest,
  PersonaSummary,
  ProfileResponse,
  RegisterRequest,
} from '../types/api'

interface AuthContextValue {
  token: string | null
  profile: ProfileResponse | null
  /** Every role this account holds - the header avatar switcher reads this. */
  roles: PersonaSummary[]
  loading: boolean
  /** Sign-in step 2: the email and the join code sent to it. */
  login: (body: LoginRequest) => Promise<void>
  /**
   * Sign-up step 1: checks the form and emails a join code. Nothing is created yet - finish with
   * verifyRegistration. (An email that already has an account gets the role added at step 2.)
   */
  register: (body: RegisterRequest) => Promise<CodeSentResponse>
  /** Sign-up step 2: the join code creates the account (or adds the role) and signs in. */
  verifyRegistration: (email: string, code: string) => Promise<void>
  /** Starts a session from a token the caller already has. */
  applyToken: (token: string) => Promise<void>
  /** Moves this session to the caller's other profile - see DualProfileService. */
  switchProfile: (profileId: string) => Promise<void>
  /** Opens this person's other side and lands the session on it. */
  addProfile: (body: AddProfileRequest) => Promise<void>
  /** Adds a Student, Tutor or Parent/Guardian role to this account and lands the session on it. */
  addRole: (body: AddRoleRequest) => Promise<void>
  logout: () => void
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

/** When a session token expires, in epoch milliseconds - read from its payload; null if unreadable. */
function tokenExpiry(token: string | null): number | null {
  try {
    const payload = token?.split('.')[1]
    if (!payload) return null
    const exp = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))).exp
    return typeof exp === 'number' ? exp * 1000 : null
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => getToken())
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [roles, setRoles] = useState<PersonaSummary[]>([])
  const [loading, setLoading] = useState(true)

  /**
   * Best-effort: a broken roles fetch must not take a working session down with it, since the
   * avatar switcher degrading to "just the one role" is a much smaller problem than being signed
   * out.
   */
  const loadRoles = useCallback(async () => {
    try {
      setRoles(await profileApi.listRoles())
    } catch {
      setRoles([])
    }
  }, [])

  const refreshProfile = useCallback(async () => {
    if (!getToken()) {
      setProfile(null)
      setRoles([])
      return
    }
    try {
      const p = await profileApi.getOwn()
      setProfile(p)
      await loadRoles()
    } catch {
      // token invalid/expired - clear it
      persistToken(null)
      setTokenState(null)
      setProfile(null)
      setRoles([])
    }
  }, [loadRoles])

  useEffect(() => {
    refreshProfile().finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The website's session lasts 120 minutes (JwtService, X-Client: web), so it is renewed while the
  // person is using the site: on a click, key or scroll - at most once a minute - a token with under
  // 20 minutes left is swapped for a fresh one. Leave the site idle for 120 minutes and it expires.
  useEffect(() => {
    if (!token) return
    let busy = false
    let lastCheck = 0
    const onActivity = () => {
      const now = Date.now()
      if (busy || now - lastCheck < 60_000) return
      lastCheck = now
      const exp = tokenExpiry(getToken())
      if (exp === null || exp <= now || exp - now > 20 * 60_000) return
      busy = true
      profileApi
        .refreshSession()
        .then((res) => {
          if (res.token) {
            persistToken(res.token)
            setTokenState(res.token)
          }
        })
        // A failed renewal is not a sign-out: the current token stays good until it expires.
        .catch(() => {})
        .finally(() => {
          busy = false
        })
    }
    const events = ['pointerdown', 'keydown', 'scroll'] as const
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }))
    return () => events.forEach((e) => window.removeEventListener(e, onActivity))
  }, [token])

  /**
   * Takes a session token off a response and makes it this browser's.
   *
   * <p>The null check is not defensive padding: AuthResponse carries a nullable token now, because
   * registration deliberately returns none. Every caller here is a path that must have one, so a
   * null is a server-side bug - and storing it would clear the session of whoever was already
   * signed in and leave the app looking logged out for no stated reason.
   */
  const applyAuthResponse = useCallback(async (res: { token: string | null }) => {
    if (!res.token) {
      throw new Error('The server returned no session token')
    }
    persistToken(res.token)
    setTokenState(res.token)
    // Loaded here rather than through refreshProfile, which swallows the failure and quietly
    // signs out - right for a stale token on page load, but on a sign-in it leaves the person on
    // the login form with a correct code and no idea why nothing happened.
    try {
      setProfile(await profileApi.getOwn())
      await loadRoles()
    } catch (err) {
      persistToken(null)
      setTokenState(null)
      setProfile(null)
      setRoles([])
      const status = err instanceof ApiError ? err.status : 0
      throw new ApiError(
        status,
        status === 404
          ? 'Your code is correct, but this profile could not be opened yet. Try signing in as your other role, or contact support.'
          : 'Signed in, but your profile could not be loaded. Please try again.',
      )
    }
  }, [loadRoles])

  const login = useCallback(async (body: LoginRequest) => {
    const res = await authApi.login(body)
    await applyAuthResponse(res)
  }, [applyAuthResponse])

  const register = useCallback((body: RegisterRequest) => authApi.register(body), [])

  const verifyRegistration = useCallback(async (email: string, code: string) => {
    await applyAuthResponse(await authApi.verifyRegistration({ email, code }))
  }, [applyAuthResponse])

  const applyToken = useCallback(async (token: string) => {
    await applyAuthResponse({ token })
  }, [applyAuthResponse])

  // Both of these are a login in everything but name: the server hands back a token for a
  // different participant, and applyAuthResponse swaps it in and reloads the profile. Every
  // persona-dependent thing in the app - which map, which nav, which half of the profile page -
  // reads that profile, so they all follow without knowing a switch happened.
  const switchProfile = useCallback(async (profileId: string) => {
    await applyAuthResponse(await profileApi.switchTo(profileId))
  }, [applyAuthResponse])

  const addProfile = useCallback(async (body: AddProfileRequest) => {
    await applyAuthResponse(await profileApi.addRole(body))
  }, [applyAuthResponse])

  const addRole = useCallback(async (body: AddRoleRequest) => {
    await applyAuthResponse(await profileApi.addSwitchableRole(body))
  }, [applyAuthResponse])

  const logout = useCallback(() => {
    persistToken(null)
    setTokenState(null)
    setProfile(null)
    setRoles([])
  }, [])

  return (
    <AuthContext.Provider
      value={{
        token,
        profile,
        roles,
        loading,
        login,
        register,
        verifyRegistration,
        applyToken,
        switchProfile,
        addProfile,
        addRole,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
